const passwordStrength = {
  minimumLength: {
    min: 12,
    weight: 15,
    strength(password: string) {
      if (password) {
        const { length } = password;
        if (length < this.min) {
          return 0;
        }
        return this.weight;
      }
      return 0;
    },
  },
  uppercaseLetters: {
    min: 1,
    weight: 5,
    strength(password: string) {
      if (password) {
        const matches = password.match(/[A-Z]/g);
        if (!matches) {
          return 0;
        }
        return matches.length >= this.min
          ? this.weight
          : (matches.length / this.min) * this.weight;
      }
      return 0;
    },
  },
  lowercaseLetters: {
    min: 1,
    weight: 5,
    strength(password: string) {
      if (password) {
        const matches = password.match(/[a-z]/g);
        if (!matches) {
          return 0;
        }
        return matches.length >= this.min
          ? this.weight
          : (matches.length / this.min) * this.weight;
      }
      return 0;
    },
  },
  symbols: {
    min: 1,
    weight: 5,
    strength(password: string) {
      if (password) {
        const matches = password.match(/[$-/:-?{-~!"^_`[\]]/g);
        if (!matches) {
          return 0;
        }
        return matches.length >= this.min
          ? this.weight
          : (matches.length / this.min) * this.weight;
      }
      return 0;
    },
  },
  numbers: {
    min: 1,
    weight: 5,
    strength(password: string) {
      if (password) {
        const matches = password.match(/[0-9]/g);
        if (!matches) {
          return 0;
        }
        return matches.length >= this.min
          ? this.weight
          : (matches.length / this.min) * this.weight;
      }
      return 0;
    },
  },
  duplicates: {
    max: 3,
    weight: -30,
    strength(password: string) {
      if (password) {
        let strength = 0;
        const matches = password.match(
          new RegExp(`(.)\\1{${this.max},}`, 'g')
        );
        if (matches) {
          matches.forEach((match) => {
            strength += (match.length - this.max) * this.weight;
          });
        }
        return strength;
      }
      return 0;
    },
  },
  consecutive: {
    max: 3,
    weight: -10,
    strength(password: string) {
      if (password) {
        let strength = 0;
        let lastChar = 0;
        let inc = true;
        let count = 0;
        for (let i = 0; i <= password.length; i += 1) {
          let charCode = -99;
          if (i < password.length) {
            charCode = password.charCodeAt(i);
          }
          if (charCode === lastChar + 1) {
            if (inc) {
              count += 1;
            } else {
              count = 2;
            }
            inc = true;
          } else if (charCode === lastChar - 1) {
            if (!inc) {
              count += 1;
            } else {
              count = 2;
            }
            inc = false;
          } else {
            strength +=
                count > this.max
                  ? (count - this.max) * this.weight
                  : 0;
            count = 1;
          }
          lastChar = charCode;
        }
        return strength;
      }
      return 0;
    },
  },
  dictionary: {
    words: [
      'asdf',
      'password',
      'qwerty',
      'monkey',
      'letmein',
      'dragon',
      'baseball',
      'iloveyou',
      'trustno1',
      'sunshine',
      'master',
      'welcome',
      'shadow',
      'ashley',
      'football',
      'jesus',
      'michael',
      'ninja',
      'mustang',
      'test',
    ],
    weight: -40,
    strength(password: string) {
      if (password) {
        let strength = 0;
        this.words.forEach((word) => {
          strength += password.indexOf(word) >= 0
            ? this.weight
            : 0;
        });
        return strength;
      }
      return 0;
    },
  },
};

/**
 * Compute the password's strength
 * @param {string} password
 * @returns {number} the strength percent
 */
/* eslint-disable no-restricted-syntax */
export function computeStrength(password: string): number {
  let total = 0;
  let strength = 0;

  for (const aspect of Object.values(passwordStrength)) {
    if (aspect.weight !== 0) {
      total += aspect.weight > 0 ? aspect.weight : 0;
      strength += aspect.strength(password);
    }
  }

  return total > 0 ? (strength / total) * 100 : 0;
}

/* eslint-disable no-nested-ternary */
export function getStrength(password: string): number {
  const percent = computeStrength(password);
  return percent > 100 ? 100 : percent < 0 ? 0 : percent;
}

export function isStrongEnough(password: string, minStrength: number): boolean {
  return computeStrength(password) >= minStrength;
}
